<template>
  <PublicPageShell shell-class="home-page">
    <template #header>
      <nav aria-label="Primary navigation" class="home-container home-nav">
        <RouterLink
          to="/"
          data-home-brand
          aria-label="PermitPilott home"
          class="home-brand"
        >
          <AppLogo :size="44" />
          <span data-home-wordmark
            >PERMIT<span class="home-orange">PILOTT</span></span
          >
        </RouterLink>
        <div class="home-desktop-nav">
          <RouterLink to="/pricing">Pricing</RouterLink>
          <RouterLink to="/login">Sign in</RouterLink>
          <RouterLink to="/signup" class="home-button">Start free</RouterLink>
        </div>
        <div class="home-mobile-nav">
          <RouterLink to="/login">Sign in</RouterLink>
          <UiMenu
            v-model="mobileMenuOpen"
            :trigger-label="
              mobileMenuOpen
                ? 'Close primary navigation'
                : 'Open primary navigation'
            "
            panel-label="Primary navigation"
            class="relative"
            data-home-mobile-menu
            trigger-class="home-menu-trigger"
            panel-class="absolute right-0 top-full z-30 w-56 rounded-xl border border-white/10 bg-[var(--bg-secondary)] p-2 shadow-xl"
          >
            <template #trigger="{ open }"
              ><UiIcon :name="open ? 'x' : 'menu'" :size="28"
            /></template>
            <template #panel="{ close }">
              <RouterLink to="/pricing" role="menuitem" @click="close()"
                >Pricing</RouterLink
              >
              <RouterLink to="/login" role="menuitem" @click="close()"
                >Sign in</RouterLink
              >
              <RouterLink to="/signup" role="menuitem" @click="close()"
                >Start free</RouterLink
              >
            </template>
          </UiMenu>
        </div>
      </nav>
    </template>
    <section data-home-hero class="home-container home-hero">
      <div class="home-intro">
        <h1>Turn your permit <span class="home-orange">into a route.</span></h1>
        <p>
          Upload your oversize or overweight permit. Review the waypoints, then
          navigate or export.
        </p>
        <RouterLink to="/signup" data-home-hero-primary class="home-button"
          >Start free</RouterLink
        >
        <p data-signup-assurance class="home-assurance">
          3 free routes · No credit card required
        </p>
        <a href="#sample-route" data-home-hero-secondary class="home-text-link"
          >Try the interactive demo <UiIcon name="arrow-down" :size="18"
        /></a>
      </div>
      <figure data-home-route-example class="home-example">
        <h2>Sample permit and route map</h2>
        <div class="home-example-images">
          <a
            href="/examples/texas-sample-permit.pdf"
            class="route-example-permit"
            aria-label="View sample permit"
            aria-haspopup="dialog"
            @click.prevent="openExample('permit')"
          >
            <div class="route-example-paper">
              <img
                src="/examples/texas-sample-instructions.webp"
                alt="Fictional Texas permit instructions, sample only, not valid for travel"
                width="831"
                height="667"
                fetchpriority="high"
              />
            </div>
          </a>
          <a
            href="/examples/texas-sample-route.png"
            class="route-example-map"
            aria-label="Enlarge route map"
            aria-haspopup="dialog"
            @click.prevent="openExample('map')"
          >
            <img
              src="/examples/texas-sample-route-desktop.webp"
              alt="Sample route map from Burnet through Lampasas to Belton"
              width="1440"
              height="1162"
              fetchpriority="high"
            />
          </a>
        </div>
        <figcaption>
          <strong>Burnet → Belton</strong
          ><span
            >Fictional Texas permit ·
            <span class="home-desktop-caption"
              >Route shown after waypoint review</span
            ><span class="home-mobile-caption">Route map</span></span
          >
        </figcaption>
      </figure>
    </section>
    <section
      id="workflow"
      aria-label="How it works"
      class="home-container home-workflow"
    >
      <ol>
        <li
          v-for="(step, index) in workflowSteps"
          :key="step.title"
          data-home-workflow-step
        >
          <span class="home-step-number">0{{ index + 1 }}</span>
          <div>
            <h2>{{ step.title }}</h2>
            <p>{{ step.description }}</p>
          </div>
        </li>
      </ol>
    </section>
    <section
      id="sample-route"
      aria-labelledby="home-demo-title"
      class="home-demo"
    >
      <div class="home-container">
        <span class="home-demo-badge"
          ><UiIcon name="map" :size="20" />Interactive demo</span
        >
        <h2 id="home-demo-title">Review every turn before you drive.</h2>
        <div class="home-demo-grid">
          <div>
            <HomeBridgePreview />
            <p class="home-demo-hint">
              <span class="home-desktop-caption">Select</span
              ><span class="home-mobile-caption">Tap</span> a waypoint to
              explore the controls.
            </p>
          </div>
          <div class="home-proof-list">
            <div
              v-for="proof in productProof"
              :key="proof.title"
              data-home-proof
              class="home-proof"
            >
              <UiIcon :name="proof.icon" :size="36" :stroke-width="1.7" />
              <div>
                <h3>{{ proof.title }}</h3>
                <p>{{ proof.description }}</p>
              </div>
            </div>
          </div>
        </div>
        <div class="home-exports">
          <div
            v-for="target in exportTargets"
            :key="target.label"
            data-home-export
          >
            <UiIcon
              :name="target.icon"
              :size="29"
              :stroke-width="1.7"
            /><span>{{ target.label }}</span>
          </div>
        </div>
      </div>
    </section>
    <section class="home-container home-faq" aria-labelledby="home-faq-title">
      <h2 id="home-faq-title">Before you start</h2>
      <div v-for="item in questions" :key="item.question" class="home-faq-row">
        <h3>{{ item.question }}</h3>
        <p>{{ item.answer }}</p>
      </div>
    </section>
    <section class="home-cta">
      <h2>Put your next permit to work.</h2>
      <p>Start with 3 free routes. No credit card required.</p>
      <div>
        <RouterLink to="/signup" data-home-bottom-primary class="home-button"
          >Start free</RouterLink
        ><RouterLink to="/pricing" class="home-text-link"
          >View pricing <UiIcon name="arrow-right" :size="18"
        /></RouterLink>
      </div>
    </section>
    <UiDialog
      v-model="exampleOpen"
      :aria-label="exampleTitle"
      initial-focus="[data-example-close]"
      :panel-scroll="false"
      overlay-class="example-viewer-overlay"
      panel-class="example-viewer-panel flex h-[90dvh] max-h-[90dvh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-white/20 bg-[#0b1629] shadow-2xl"
    >
      <div
        class="flex shrink-0 items-center justify-between gap-4 border-b border-white/10 px-4 py-3"
      >
        <h2
          id="example-viewer-title"
          class="text-base font-semibold text-white"
        >
          {{ exampleTitle }}
        </h2>
        <button
          data-example-close
          type="button"
          class="inline-flex min-h-11 shrink-0 items-center gap-2 rounded-lg border border-white/25 px-3 text-sm font-semibold text-white hover:bg-white/10"
          @click="exampleOpen = false"
        >
          <UiIcon name="x" :size="18" />
          Close
        </button>
      </div>
      <div
        class="flex shrink-0 items-center justify-center gap-3 border-b border-white/10 py-1"
      >
        <button
          type="button"
          aria-label="Zoom out"
          :disabled="exampleZoom === 1"
          class="flex h-11 w-11 items-center justify-center rounded-lg text-white hover:bg-white/10"
          @click="changeExampleZoom(-0.5)"
        >
          <UiIcon name="minus" :size="20" />
        </button>
        <output class="w-14 text-center text-sm tabular-nums text-white"
          >{{ Math.round(exampleZoom * 100) }}%</output
        >
        <button
          type="button"
          aria-label="Zoom in"
          :disabled="exampleZoom === 4"
          class="flex h-11 w-11 items-center justify-center rounded-lg text-white hover:bg-white/10"
          @click="changeExampleZoom(0.5)"
        >
          <UiIcon name="plus" :size="20" />
        </button>
      </div>
      <div
        ref="exampleViewport"
        tabindex="0"
        role="region"
        aria-labelledby="example-viewer-title"
        class="min-h-0 flex-1 overflow-auto overscroll-contain p-2 sm:p-4"
      >
        <div class="relative" :style="{ width: `${exampleZoom * 100}%` }">
          <div v-if="selectedExample === 'permit'" class="flex flex-col gap-4">
            <img
              v-for="page in [2, 1]"
              :key="page"
              :data-permit-page="page"
              :src="`/examples/texas-sample-permit-page-${page}.png`"
              alt=""
              width="928"
              height="1200"
              class="block h-auto w-full"
            />
          </div>
          <img
            v-else
            src="/examples/texas-sample-route.png"
            alt=""
            width="1440"
            height="1162"
            class="block h-auto w-full"
          />
        </div>
      </div>
    </UiDialog>
  </PublicPageShell>
</template>

<script setup>
import { computed, nextTick, ref } from "vue";
import { RouterLink } from "vue-router";
import AppLogo from "../components/AppLogo.vue";
import PublicPageShell from "../components/PublicPageShell.vue";
import HomeBridgePreview from "../components/HomeBridgePreview.vue";
import UiIcon from "../components/ui/UiIcon.vue";
import UiDialog from "../components/ui/UiDialog.vue";
import UiMenu from "../components/ui/UiMenu.vue";

const mobileMenuOpen = ref(false);
const exampleOpen = ref(false);
const selectedExample = ref("permit");
const exampleTitle = computed(() =>
  selectedExample.value === "permit" ? "Sample Permit" : "Route Map",
);

const exampleZoom = ref(1);
const exampleViewport = ref(null);

async function changeExampleZoom(delta) {
  const viewport = exampleViewport.value;
  const previousZoom = exampleZoom.value;
  const centerX = viewport.scrollLeft + viewport.clientWidth / 2;
  const centerY = viewport.scrollTop + viewport.clientHeight / 2;
  exampleZoom.value = Math.max(1, Math.min(4, previousZoom + delta));
  await nextTick();
  const ratio = exampleZoom.value / previousZoom;
  viewport.scrollLeft = centerX * ratio - viewport.clientWidth / 2;
  viewport.scrollTop = centerY * ratio - viewport.clientHeight / 2;
}

function openExample(kind) {
  selectedExample.value = kind;
  exampleZoom.value = 1;
  exampleOpen.value = true;
}

const workflowSteps = [
  { title: "Upload your permit", description: "Start with a PDF or photo." },
  {
    title: "Review the route",
    description: "Compare roads and adjust waypoints.",
  },
  {
    title: "Navigate or export",
    description: "Use in-app navigation or export GPX.",
  },
];
const productProof = [
  {
    icon: "map",
    title: "Check the permitted roads",
    description: "Compare the route with your permit.",
  },
  {
    icon: "pin",
    title: "Adjust waypoints",
    description: "Move or add a point, then recalculate.",
  },
  {
    icon: "fuel",
    title: "Add stops",
    description:
      "Find truck stops, rest stops, weigh stations, and hotels or motels along your route.",
  },
  {
    icon: "volume",
    title: "Add useful details",
    description: "Find bridge structures and add spoken guidance.",
  },
];
const exportTargets = [
  { icon: "navigation", label: "In-app navigation" },
  { icon: "smartphone", label: "Garmin GPX" },
  { icon: "map", label: "Google Maps" },
  { icon: "apple", label: "Apple Maps" },
];
const questions = [
  { question: "What can I upload?", answer: "A permit PDF or photo." },
  {
    question: "Do I need to review the route?",
    answer:
      "Yes. Compare the roads and turns with your permit, and correct waypoints before travel.",
  },
  {
    question: "How can I use the route?",
    answer: "Navigate in-app or export a GPX file.",
  },
];
</script>

<style scoped>
.home-page {
  font-family: Arial, sans-serif;
  background: #101c2a;
  color: #f5f7fb;
}
.home-container {
  width: min(100% - 64px, 1200px);
  margin-inline: auto;
}
.home-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-block: 18px;
  gap: 24px;
}
.home-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 23px;
  font-weight: 800;
}
.home-orange {
  color: #ff710b;
}
.home-desktop-nav {
  display: flex;
  align-items: center;
  gap: 32px;
  font-size: 14px;
}
.home-desktop-nav > a {
  display: inline-flex;
  align-items: center;
  min-height: 44px;
}
.home-mobile-nav {
  display: none;
}
.home-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 54px;
  padding: 12px 36px;
  border-radius: 11px;
  background: #2075ff;
  color: white;
  font-weight: 600;
  text-align: center;
}
.home-button:hover {
  background: #1264e9;
}
.home-desktop-nav .home-button {
  min-height: 40px;
  padding: 9px 18px;
}
.home-hero {
  display: grid;
  grid-template-columns: 0.75fr 1.25fr;
  align-items: center;
  gap: 28px;
  padding-block: 16px 38px;
}
.home-intro h1 {
  font-size: clamp(36px, 4.3vw, 48px);
  line-height: 1.15;
  font-weight: 750;
  letter-spacing: -0.7px;
}
.home-intro h1 span {
  display: block;
}
.home-intro > p:first-of-type {
  margin-top: 18px;
  color: #bac7d8;
  font-size: 18px;
  line-height: 1.5;
  max-width: 420px;
}
.home-intro .home-button {
  margin-top: 28px;
  min-width: 208px;
}
.home-intro .home-assurance {
  font-size: 14px;
  color: #bac7d8;
  margin-top: 14px;
}
.home-text-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #49adff;
  min-height: 44px;
  font-size: 16px;
}
.home-intro > .home-text-link {
  margin-top: 26px;
}
.home-example {
  min-width: 0;
  background: #1c2b3e;
  border-radius: 16px;
  padding: 18px;
}
.home-example h2 {
  font-size: 18px;
  line-height: 1.4;
  font-weight: 600;
  margin-bottom: 12px;
}
.home-example-images {
  display: grid;
  grid-template-columns: 0.9fr 1.1fr;
  gap: 12px;
  align-items: stretch;
}
.home-example-images a {
  min-width: 0;
  align-self: center;
}
.home-example-images .route-example-map {
  overflow: hidden;
  border: 1px solid #8090a4;
  border-radius: 6px;
  background: white;
}
.home-example-images img {
  display: block;
  width: 100%;
  height: auto;
}
.route-example-permit {
  padding: 10px;
}
.route-example-paper {
  position: relative;
  isolation: isolate;
  width: 100%;
  transform: rotate(-3deg);
  border: 6px solid white;
  background: white;
  box-shadow: 0 12px 28px #0005;
  transition: transform 180ms ease;
}
.route-example-paper::before,
.route-example-paper::after {
  position: absolute;
  z-index: -1;
  inset: -6px;
  content: "";
  border: 1px solid #cbd5e1;
  background: #e2e8f0;
  transform: rotate(3deg);
}
.route-example-paper::after {
  background: #f8fafc;
  transform: rotate(1.5deg);
}
.route-example-permit:is(:hover, :focus-visible) .route-example-paper {
  transform: rotate(-1deg) translateY(-3px);
}
@media (prefers-reduced-motion: reduce) {
  .route-example-paper {
    transition: none;
  }
  .route-example-permit:is(:hover, :focus-visible) .route-example-paper {
    transform: rotate(-3deg);
  }
}

.home-example figcaption {
  color: #bac7d8;
  font-size: 12px;
  margin-top: 8px;
}
.home-example figcaption strong,
.home-mobile-caption {
  display: none;
}
.home-workflow {
  border-top: 1px solid #344357;
  padding-block: 28px;
}
.home-workflow ol {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
}
.home-workflow li {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  padding: 6px 24px;
  border-left: 1px solid #344357;
}
.home-workflow li:first-child {
  padding-left: 0;
  border: 0;
}
.home-workflow li:last-child {
  padding-right: 0;
}
.home-step-number {
  font-size: 40px;
  line-height: 1.15;
  font-weight: 650;
  color: #ff710b;
}
.home-workflow h2,
.home-proof h3 {
  font-size: 16px;
  font-weight: 650;
  margin-bottom: 4px;
}
.home-workflow p,
.home-proof p {
  color: #bac7d8;
  font-size: 14px;
  line-height: 1.5;
}
.home-demo {
  margin-top: 0;
  padding-block: 30px 24px;
  background: #1a293b;
  border-radius: 18px;
  scroll-margin-top: 20px;
}
.home-demo-badge {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  padding: 7px 14px;
  border-radius: 24px;
  background: #183965;
  font-size: 14px;
  font-weight: 600;
}
.home-demo h2,
.home-faq > h2,
.home-cta h2 {
  font-size: 32px;
  font-weight: 700;
  line-height: 1.25;
  letter-spacing: -0.4px;
}
.home-demo h2 {
  margin-block: 12px 24px;
}
.home-demo-grid {
  display: grid;
  grid-template-columns: 1.78fr 1fr;
  gap: 50px;
}
.home-demo-hint {
  font-size: 14px;
  color: #bac7d8;
  margin-top: 10px;
}
.home-proof-list {
  display: flex;
  flex-direction: column;
  justify-content: space-around;
}
.home-proof {
  display: flex;
  align-items: flex-start;
  gap: 24px;
  padding-block: 20px;
  border-bottom: 1px solid #344357;
}
.home-proof:last-child {
  border: 0;
}
.home-proof > svg {
  color: #ff7900;
  flex-shrink: 0;
}
.home-exports {
  border-top: 1px solid #344357;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-top: 42px;
  padding-block: 24px;
}
.home-exports > div {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 22px;
  font-size: 14px;
}
.home-exports svg {
  color: #d6e2f4;
  flex-shrink: 0;
}
.home-exports .lucide-apple {
  fill: currentColor;
}
.home-faq {
  padding-block: 38px;
}
.home-faq > h2 {
  margin-bottom: 18px;
}
.home-faq-row {
  position: relative;
  display: grid;
  grid-template-columns: 35% 65%;
  padding-block: 22px;
  border-bottom: 1px solid #344357;
}
.home-faq-row h3 {
  text-align: left;
  font-size: 16px;
  font-weight: 650;
  padding-right: 20px;
}
.home-faq-row p {
  padding-right: 40px;
  font-size: 16px;
  line-height: 1.6;
  color: #bac7d8;
}
.home-cta {
  width: min(100% - 32px, 1248px);
  margin-inline: auto;
  margin-bottom: 40px;
  background: #1c2b3e;
  border-radius: 16px;
  padding: 30px 36px;
}
.home-cta > p {
  font-size: 20px;
  color: #d0dcec;
  margin-top: 10px;
}
.home-cta > div {
  display: flex;
  align-items: center;
  gap: 30px;
  margin-top: 26px;
}
.home-cta .home-button {
  min-width: 182px;
}
a:focus-visible,
button:focus-visible {
  outline: 2px solid #78baff;
  outline-offset: 4px;
}
@media (min-width: 1440px) {
  .home-demo {
    width: 1280px;
    margin-inline: auto;
  }
}
@media (max-width: 767px) {
  .home-container {
    width: min(100% - 40px, 484px);
  }
  .home-nav {
    padding-block: 12px;
  }
  [data-home-wordmark],
  .home-desktop-nav {
    display: none;
  }
  .home-mobile-nav {
    display: flex;
    align-items: center;
    gap: 22px;
  }
  .home-mobile-nav :deep(.home-menu-trigger) {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
  }
  .home-mobile-nav [role="menuitem"] {
    display: flex;
    align-items: center;
    min-height: 44px;
    padding-inline: 12px;
  }
  .home-hero {
    grid-template-columns: 1fr;
    gap: 0;
    padding-block: 6px 12px;
  }
  .home-intro h1 {
    font-size: clamp(32px, 6.7vw, 40px);
    line-height: 1.12;
  }
  .home-intro > p:first-of-type {
    font-size: 17px;
    line-height: 1.45;
    margin-top: 12px;
    max-width: none;
  }
  .home-intro .home-button {
    width: 100%;
    margin-top: 18px;
    min-height: 48px;
  }
  .home-intro .home-assurance {
    text-align: center;
    margin-top: 8px;
    font-size: 14px;
  }
  .home-intro > .home-text-link {
    display: flex;
    margin-top: 4px;
  }
  .home-example {
    padding: 12px;
    margin-inline: -8px;
    border-radius: 14px;
  }
  .home-desktop-caption {
    display: none;
  }
  .home-mobile-caption {
    display: inline;
  }
  .home-example-images {
    gap: 8px;
  }
  .home-example figcaption {
    font-size: 13px;
  }
  .home-example figcaption strong {
    display: block;
    color: #eef3fa;
    font-size: 16px;
    margin-bottom: 2px;
  }
  .home-workflow {
    border: 0;
    padding-block: 0 14px;
  }
  .home-workflow ol {
    grid-template-columns: 1fr;
  }
  .home-workflow li,
  .home-workflow li:first-child {
    border: 0;
    border-bottom: 1px solid #344357;
    padding: 13px 0;
    gap: 22px;
  }
  .home-workflow li:last-child {
    border: 0;
  }
  .home-step-number {
    font-size: 34px;
  }
  .home-workflow h2,
  .home-proof h3 {
    font-size: 16px;
    margin-bottom: 2px;
  }
  .home-workflow p,
  .home-proof p {
    font-size: 14px;
  }
  .home-demo {
    width: min(100% - 16px, 536px);
    margin-inline: auto;
    padding-block: 14px 12px;
    border-radius: 15px;
  }
  .home-demo .home-container {
    width: min(100% - 24px, 484px);
  }
  .home-demo h2,
  .home-faq > h2,
  .home-cta h2 {
    font-size: clamp(24px, 4.6vw, 30px);
  }
  .home-demo h2 {
    margin-block: 8px 12px;
  }
  .home-demo-grid {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  .home-demo-hint {
    font-size: 12px;
    margin-top: 8px;
  }
  .home-proof {
    padding-block: 10px;
    border: 0;
    gap: 22px;
  }
  .home-proof > svg {
    width: 32px;
    height: 32px;
  }
  .home-exports {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px 12px;
    margin-top: 8px;
    padding-block: 16px 4px;
  }
  .home-exports > div {
    justify-content: flex-start;
    gap: 18px;
    font-size: 13px;
  }
  .home-faq {
    padding-block: 24px 18px;
  }
  .home-faq > h2 {
    margin-bottom: 4px;
  }
  .home-faq-row {
    display: block;
    padding-block: 12px;
  }
  .home-faq-row h3 {
    width: 100%;
    font-size: 15px;
    min-height: 24px;
    padding-right: 28px;
  }
  .home-faq-row p {
    font-size: 14px;
    padding-right: 16px;
    line-height: 1.5;
  }
  .home-faq-row:last-child {
    border: 0;
  }
  .home-cta {
    width: min(100% - 16px, 536px);
    margin-bottom: 24px;
    padding: 22px max(12px, calc((100% - 484px) / 2)) 12px;
  }
  .home-cta > p {
    font-size: 16px;
  }
  .home-cta > div {
    flex-direction: column;
    align-items: stretch;
    gap: 0;
    margin-top: 14px;
  }
  .home-cta .home-button {
    min-height: 48px;
  }
}
@media (min-width: 480px) and (max-width: 767px) {
  .home-intro > p:first-of-type {
    font-size: 20px;
  }
  .home-workflow h2,
  .home-proof h3 {
    font-size: 18px;
  }
  .home-workflow p,
  .home-proof p,
  .home-faq-row p {
    font-size: 16px;
  }
  .home-assurance,
  .home-example figcaption,
  .home-exports > div {
    font-size: 15px;
  }
  .home-faq-row h3 {
    font-size: 17px;
  }
  .home-cta {
    padding-inline: 26px;
  }
}
</style>
